import { useState } from 'react';
import { Client } from '@/types/client';
import { StatusBadge, BooleanBadge } from '@/components/ui/StatusBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Pencil, Trash2, Eye, MoreHorizontal, Mail } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ClientTableProps {
  clients: Client[];
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
  onView: (client: Client) => void;
  onEmail: (client: Client) => void;
}

export function ClientTable({ clients, onEdit, onDelete, onView, onEmail }: ClientTableProps) {
  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      <ScrollArea className="w-full">
        <div className="min-w-[2400px]">
          <Table>
            <TableHeader>
              <TableRow className="bg-primary/5 hover:bg-primary/5">
                <TableHead className="w-[60px] font-semibold text-primary">Sr.</TableHead>
                <TableHead className="min-w-[150px] font-semibold text-primary">Director / Proprietor</TableHead>
                <TableHead className="min-w-[130px] font-semibold text-primary">Nature of Business</TableHead>
                <TableHead className="min-w-[180px] font-semibold text-primary">Company Name</TableHead>
                <TableHead className="min-w-[100px] font-semibold text-primary">Client Type</TableHead>
                <TableHead className="min-w-[80px] font-semibold text-primary">GST</TableHead>
                <TableHead className="min-w-[130px] font-semibold text-primary">ABN</TableHead>
                <TableHead className="min-w-[110px] font-semibold text-primary">TFN</TableHead>
                <TableHead className="min-w-[180px] font-semibold text-primary">Email</TableHead>
                <TableHead className="min-w-[100px] font-semibold text-primary">Team</TableHead>
                <TableHead className="min-w-[120px] font-semibold text-primary">Employee</TableHead>
                <TableHead className="min-w-[100px] font-semibold text-primary">Statement</TableHead>
                <TableHead className="min-w-[110px] font-semibold text-primary">BAS Status</TableHead>
                <TableHead className="min-w-[110px] font-semibold text-primary">BAS Return</TableHead>
                <TableHead className="min-w-[110px] font-semibold text-primary">Payroll</TableHead>
                <TableHead className="min-w-[110px] font-semibold text-primary">STP File</TableHead>
                <TableHead className="min-w-[110px] font-semibold text-primary">Super</TableHead>
                <TableHead className="min-w-[100px] font-semibold text-primary">Software</TableHead>
                <TableHead className="min-w-[90px] font-semibold text-primary">Handy</TableHead>
                <TableHead className="min-w-[120px] font-semibold text-primary">Bookkeeping</TableHead>
                <TableHead className="min-w-[110px] font-semibold text-primary">Invoices</TableHead>
                <TableHead className="min-w-[200px] font-semibold text-primary">Remarks</TableHead>
                <TableHead className="w-[80px] font-semibold text-primary sticky right-0 bg-primary/5">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={23} className="text-center py-12 text-muted-foreground">
                    No clients found. Add your first client to get started.
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client, index) => (
                  <TableRow 
                    key={client.id} 
                    className="hover:bg-muted/50 transition-colors animate-fade-in"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <TableCell className="font-medium text-muted-foreground">{client.srNo}</TableCell>
                    <TableCell className="font-medium">{client.directorName}</TableCell>
                    <TableCell className="text-muted-foreground">{client.natureOfBusiness}</TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="font-medium truncate block max-w-[160px]">
                            {client.companyName}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{client.companyName}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-secondary/20 text-secondary-foreground">
                        {client.clientType}
                      </span>
                    </TableCell>
                    <TableCell>
                      <BooleanBadge value={client.gstFilling} />
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{client.abn}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{client.tfn}</TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href={`mailto:${client.emailAddress}`}
                            className="text-info hover:underline truncate block max-w-[160px]"
                          >
                            {client.emailAddress}
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>{client.emailAddress}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{client.team}</TableCell>
                    <TableCell className="text-muted-foreground">{client.employee}</TableCell>
                    <TableCell>
                      <BooleanBadge value={client.statementReceived} trueLabel="Received" falseLabel="Pending" />
                    </TableCell>
                    <TableCell><StatusBadge status={client.basStatus} /></TableCell>
                    <TableCell><StatusBadge status={client.basReturn} /></TableCell>
                    <TableCell><StatusBadge status={client.payrollPayslip} /></TableCell>
                    <TableCell><StatusBadge status={client.stpFile} /></TableCell>
                    <TableCell><StatusBadge status={client.superPayment} /></TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{client.bookkeepingSoftware}</span>
                    </TableCell>
                    <TableCell>
                      <BooleanBadge value={client.handyLedger} />
                    </TableCell>
                    <TableCell><StatusBadge status={client.bookkeepingStatus} /></TableCell>
                    <TableCell><StatusBadge status={client.invoices} /></TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm text-muted-foreground truncate block max-w-[180px]">
                            {client.remarks || '—'}
                          </span>
                        </TooltipTrigger>
                        {client.remarks && <TooltipContent className="max-w-xs">{client.remarks}</TooltipContent>}
                      </Tooltip>
                    </TableCell>
                    <TableCell className="sticky right-0 bg-card">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onView(client)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onEmail(client)}>
                            <Mail className="h-4 w-4 mr-2" />
                            Send Status Report
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onEdit(client)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => onDelete(client)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
